import React, { useState, useEffect, useRef } from 'react';
import {
    Search,
    UserPlus,
    Mail,
    Phone,
    MoreVertical,
    X,
    CheckCircle,
    Clock,
    UserX,
    Send,
    Eye,
    BookOpen
} from 'lucide-react';
import { useAdminStore } from '../../stores/adminStore';
import { employeeService, Employee } from '../../services/employeeService';
import { useNavigate } from 'react-router-dom';

// const ROLES = ['Employee', 'Admin'] as const;

interface InviteFormData {
    name: string;
    email: string;
    department: string;
    role: string;
    curriculum: string;
}

interface InviteModalProps {
    show: boolean;
    onClose: () => void;
    onSubmit: (data: { name: string; email: string; curriculum_id: string }) => Promise<void>;
}

const InviteModal = ({ show, onClose, onSubmit }: InviteModalProps) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [curriculumId, setCurriculumId] = useState('');
    const [formErrors, setFormErrors] = useState<Partial<InviteFormData>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = (): boolean => {
        const errors: Partial<InviteFormData> = {};
        if (!name.trim()) {
            errors.name = 'Name is required';
        }
        if (!email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{3,}$/.test(email)) {
            errors.email = 'Please enter a valid email address (e.g., user@example.com)';
        }
        if (!curriculumId.trim()) {
            errors.curriculum = 'Curriculum is required';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateEmail = (emailValue: string) => {
        if (!emailValue.trim()) {
            setFormErrors(prev => ({ ...prev, email: 'Email is required' }));
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{3,}$/.test(emailValue)) {
            setFormErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
        } else {
            setFormErrors(prev => ({ ...prev, email: undefined }));
        }
    };

    const resetForm = () => {
        setName('');
        setEmail('');
        setCurriculumId('');
        setFormErrors({});
    };

    const handleModalClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {
            setIsSubmitting(true);
            await onSubmit({ name, email, curriculum_id: curriculumId });
            handleModalClose();
        } catch (error) {
            console.error('Failed to invite employee:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-slate-200 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-slate-500">Invite New Employee</h3>
                    <button onClick={handleModalClose} className="text-slate-500 hover:text-slate-500">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-500 mb-1">Curriculum</label>
                        <select 
                            value={curriculumId}
                            onChange={(e) => setCurriculumId(e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                formErrors.curriculum ? 'border-red-300' : 'border-slate-300'
                            }`}
                        >
                            <option value="">Select a curriculum</option>
                            <option value="ee20f273-17ba-4635-aaf6-d359e38a4aa2">60 Day Orientation</option>
                            <option value="822991ba-d074-4d68-b32d-ea004ddd9bc8">Annual Training</option>
                        </select>
                        {formErrors.curriculum && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.curriculum}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-500 mb-1">Full Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                formErrors.name ? 'border-red-300' : 'border-slate-300'
                            }`}
                            placeholder="Enter employee's full name"
                        />
                        {formErrors.name && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-500 mb-1">Email Address</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                validateEmail(e.target.value);
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                formErrors.email ? 'border-red-300' : 'border-slate-300'
                            }`}
                            placeholder="Enter employee's email"
                        />
                        {formErrors.email && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                        )}
                    </div>
                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            type="button"
                            onClick={handleModalClose}
                            className="px-4 py-2 text-slate-500 bg-slate-100 rounded-lg hover:bg-slate-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 ${
                                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {isSubmitting ? 'Sending...' : 'Send Invitation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

interface BulkReminderModalProps {
    show: boolean;
    onClose: () => void;
    employees: Employee[];
    onSubmit: () => Promise<void>;
}

const BulkReminderModal = ({ show, onClose, employees, onSubmit }: BulkReminderModalProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            await onSubmit();
            onClose();
        } catch (error) {
            console.error('Failed to send bulk reminders:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-slate-200 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-slate-500">Bulk Course Reminders</h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-500">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="mb-6">
                    <p className="text-slate-500 mb-4">
                        The following {employees.length} employees will receive course completion reminders:
                    </p>
                    
                    <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
                        <div className="divide-y divide-slate-200">
                            {employees.map((employee) => (
                                <div key={employee.id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                            <span className="text-xs font-medium text-primary-600">
                                                {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-slate-500">{employee.name ? employee.name : 'N/A'}</div>
                                            <div className="text-xs text-slate-500">{employee.email}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-slate-500 bg-slate-100 rounded-lg hover:bg-slate-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={`px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center ${
                            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                        <Send className="w-4 h-4 mr-2" />
                        {isSubmitting ? 'Sending...' : `Send Reminders (${employees.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export function AdminEmployeePage() {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showBulkReminderModal, setShowBulkReminderModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const [actionType, setActionType] = useState<'deactivate' | 'reactivate'>('deactivate');
    const [showAssignCurriculumModal, setShowAssignCurriculumModal] = useState(false);
    const [assignCurriculumEmployee, setAssignCurriculumEmployee] = useState<Employee | null>(null);
    const [assignCurriculumId, setAssignCurriculumId] = useState('');
    const [isAssigningCurriculum, setIsAssigningCurriculum] = useState(false);
    const { employees, isLoading, error, fetchAdminData } = useAdminStore();
    const navigate = useNavigate();
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenDropdownId(null);
            }
        };

        if (openDropdownId) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [openDropdownId]);

    const handleAssignCurriculum = async () => {
        if (!assignCurriculumEmployee || !assignCurriculumId) return;
        try {
            setIsAssigningCurriculum(true);
            await employeeService.assignCurriculum(assignCurriculumEmployee.id, assignCurriculumId);
            setSuccessMessage(`Curriculum assigned to ${assignCurriculumEmployee.name}`);
            setShowSuccessModal(true);
            setShowAssignCurriculumModal(false);
            setAssignCurriculumEmployee(null);
            setAssignCurriculumId('');
            await fetchAdminData();
            setTimeout(() => setShowSuccessModal(false), 3000);
        } catch (error: any) {
            console.error('Failed to assign curriculum:', error);
            setSuccessMessage(error?.message || 'Failed to assign curriculum');
            setShowSuccessModal(true);
            setTimeout(() => setShowSuccessModal(false), 3000);
        } finally {
            setIsAssigningCurriculum(false);
        }
    };

    const handleInviteSubmit = async (data: { name: string; email: string; curriculum_id: string }) => {
        try {
            await employeeService.inviteEmployee(data);
            setSuccessMessage('Employee invited successfully!');
            setShowSuccessModal(true);
            setTimeout(() => {
                setShowSuccessModal(false);
            }, 3000);
        } catch (error) {
            console.error('Failed to invite employee:', error);
        }
    };

    const handleDeactivate = async (employee: Employee) => {
        try {
            await employeeService.deactivateEmployee(employee.id);
            await fetchAdminData(); // Refresh the employee list
            setSuccessMessage('Employee deactivated successfully!');
            setShowSuccessModal(true);
            setTimeout(() => {
                setShowSuccessModal(false);
            }, 3000);
        } catch (error) {
            console.error('Failed to deactivate employee:', error);
        }
    };

    const handleReactivate = async (employee: Employee) => {
        try {
            await employeeService.reactivateEmployee(employee.id);
            await fetchAdminData(); // Refresh the employee list
            setSuccessMessage('Employee reactivated successfully!');
            setShowSuccessModal(true);
            setTimeout(() => {
                setShowSuccessModal(false);
            }, 3000);
        } catch (error) {
            console.error('Failed to reactivate employee:', error);
        }
    };

    const handleBulkReminderSubmit = async () => {
        try {
            // This is a placeholder API call - you'll need to implement this endpoint
            await employeeService.sendBulkReminders(employees.map(emp => emp.email));
            setSuccessMessage(`Bulk reminders sent successfully to ${employees.length} employees!`);
            setShowSuccessModal(true);
            setTimeout(() => {
                setShowSuccessModal(false);
            }, 3000);
        } catch (error) {
            console.error('Failed to send bulk reminders:', error);
            setSuccessMessage('Failed to send bulk reminders. Please try again.');
            setShowSuccessModal(true);
            setTimeout(() => {
                setShowSuccessModal(false);
            }, 3000);
        }
    };

    const filteredEmployees = employees.filter(employee => {
        const matchesSearch = 
            employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            employee.email.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0">
                    <div className="relative">
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            className="w-full sm:w-80 pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-3 sm:space-y-0">
                    <button
                        onClick={() => setShowBulkReminderModal(true)}
                        className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        <Mail className="w-4 h-4 mr-2" />
                        Bulk Course Reminders
                    </button>
                    <button
                        onClick={() => setShowInviteModal(true)}
                        className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Invite Employee
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-visible">
                {isLoading ? (
                    <>
                        {/* Mobile Loading Skeleton */}
                        <div className="lg:hidden p-4 space-y-4">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="p-4 border rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <div className="h-10 w-10 rounded-full bg-slate-200 animate-pulse"/>
                                        <div className="space-y-2 flex-1">
                                            <div className="h-4 w-32 bg-slate-200 rounded animate-pulse"/>
                                            <div className="h-3 w-24 bg-slate-200 rounded animate-pulse"/>
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-2">
                                        <div className="h-4 w-28 bg-slate-200 rounded animate-pulse"/>
                                        <div className="h-3 w-24 bg-slate-200 rounded animate-pulse"/>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Desktop Loading Skeleton */}
                        <div className="hidden lg:block">
                            <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                                <div className="h-4 w-24 bg-slate-200 rounded animate-pulse"/>
                            </div>
                            <div className="divide-y divide-slate-200">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="px-6 py-4 flex items-center space-x-12">
                                        <div className="flex items-center space-x-4 flex-1">
                                            <div className="h-10 w-10 rounded-full bg-slate-200 animate-pulse"/>
                                            <div className="space-y-2">
                                                <div className="h-4 w-32 bg-slate-200 rounded animate-pulse"/>
                                                <div className="h-3 w-24 bg-slate-200 rounded animate-pulse"/>
                                            </div>
                                        </div>
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 w-28 bg-slate-200 rounded animate-pulse"/>
                                            <div className="h-3 w-24 bg-slate-200 rounded animate-pulse"/>
                                        </div>
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 w-40 bg-slate-200 rounded animate-pulse"/>
                                            <div className="h-3 w-32 bg-slate-200 rounded animate-pulse"/>
                                        </div>
                                        <div className="flex-1">
                                            <div className="h-4 w-32 bg-slate-200 rounded animate-pulse"/>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Mobile View */}
                        <div className="lg:hidden p-4 space-y-4">
                            {filteredEmployees.map((employee) => (
                                <div key={employee.id} 
                                    className={`p-4 border rounded-lg ${employee.status !== 'active' ? 'opacity-60' : ''}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                                <span className="text-sm font-medium text-primary-600">
                                                    {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <div className={`font-medium text-slate-500 ${employee.status !== 'active' ? 'line-through' : ''}`}>{employee.name}</div>
                                                <div className={`text-sm text-slate-500 ${employee.status !== 'active' ? 'line-through' : ''}`}>{employee.email}</div>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedEmployee(employee);
                                                    if (employee.status === 'active') {
                                                        setActionType('deactivate');
                                                    } else {
                                                        setActionType('reactivate');
                                                    }
                                                    setShowDeactivateModal(true);
                                                }}
                                                className="text-slate-500 hover:text-slate-700 p-1 rounded hover:bg-slate-100"
                                            >
                                                <MoreVertical className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-2">
                                        <div className="flex items-center text-sm text-slate-500">
                                            <div className="w-24 text-slate-500">Department:</div>
                                            <div className={employee.status !== 'active' ? 'line-through' : ''}>{employee.department}</div>
                                        </div>
                                        <div className="flex items-center text-sm text-slate-500">
                                            <div className="w-24 text-slate-500">Role:</div>
                                            <div className={employee.status !== 'active' ? 'line-through' : ''}>{employee.role}</div>
                                        </div>
                                        {employee.phone && (
                                            <div className="flex items-center text-sm text-slate-500">
                                                <div className="w-24 text-slate-500">Phone:</div>
                                                <div className={`flex items-center ${employee.status !== 'active' ? 'line-through' : ''}`}>
                                                    <Phone className="w-4 h-4 mr-1" />
                                                    {employee.phone}
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex items-center text-sm text-slate-500">
                                            <div className="w-24 text-slate-500">Last Signed In:</div>
                                            <div className="flex items-center">
                                                {employee.status !== 'active' ? (
                                                    <span>-</span>
                                                ) : (
                                                    <>
                                                        <Clock className={`w-4 h-4 mr-1 ${employee.last_sign_in === 'Never' ? 'text-red-500 font-bold' : (employee.last_sign_in && !isNaN(parseInt(employee.last_sign_in)) && parseInt(employee.last_sign_in) > 30) ? 'font-bold' : ''}`} />
                                                        <span className={employee.last_sign_in === 'Never' ? 'font-bold text-red-500' : (employee.last_sign_in && !isNaN(parseInt(employee.last_sign_in)) && parseInt(employee.last_sign_in) > 30) ? 'font-bold' : ''}>
                                                            {employee.last_sign_in}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Desktop View */}
                        <div className="hidden lg:block overflow-visible">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                            Employee
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                            Department & Role
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                            Contact
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                            Last Signed In
                                        </th>
                                        <th className="relative px-6 py-3">
                                            <span className="sr-only">Actions</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-slate-200">
                                    {filteredEmployees.map((employee) => (
                                        <tr key={employee.id} 
                                            className={`${employee.status !== 'active' ? 'opacity-60' : ''}`}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10">
                                                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                                            <span className="text-sm font-medium text-primary-600">
                                                                {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className={`text-sm font-medium text-slate-500 ${employee.status !== 'active' ? 'line-through' : ''}`}>{employee.name}</div>
                                                        <div className={`text-sm text-slate-500 ${employee.status !== 'active' ? 'line-through' : ''}`}>{employee.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className={`text-sm text-slate-500 ${employee.status !== 'active' ? 'line-through' : ''}`}>{employee.department}</div>
                                                <div className={`text-sm text-slate-500 ${employee.status !== 'active' ? 'line-through' : ''}`}>{employee.role}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm text-slate-500 space-x-4">
                                                    {employee.email && (
                                                        <div className={`flex items-center ${employee.status !== 'active' ? 'line-through' : ''}`}>
                                                            <Mail className="w-4 h-4 mr-1" />
                                                            {employee.email}
                                                        </div>
                                                    )}
                                                    {employee.phone && (
                                                        <div className={`flex items-center ${employee.status !== 'active' ? 'line-through' : ''}`}>
                                                            <Phone className="w-4 h-4 mr-1" />
                                                            {employee.phone}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm text-slate-500">
                                                    {employee.status !== 'active' ? (
                                                        <span>-</span>
                                                    ) : (
                                                        <>
                                                            <Clock className={`w-4 h-4 mr-1 ${employee.last_sign_in === 'Never' ? 'text-red-500 font-bold' : (employee.last_sign_in && !isNaN(parseInt(employee.last_sign_in)) && parseInt(employee.last_sign_in) > 30) ? 'font-bold' : ''}`} />
                                                            <span className={employee.last_sign_in === 'Never' ? 'font-bold text-red-500' : (employee.last_sign_in && !isNaN(parseInt(employee.last_sign_in)) && parseInt(employee.last_sign_in) > 30) ? 'font-bold' : ''}>
                                                                {employee.last_sign_in}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="relative" ref={openDropdownId === employee.id ? dropdownRef : null}>
                                                    <button 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            console.log('Three-dot clicked for employee:', employee.name, 'Current openDropdownId:', openDropdownId);
                                                            setOpenDropdownId(openDropdownId === employee.id ? null : employee.id);
                                                        }}
                                                        className="text-slate-500 hover:text-slate-700 p-1 rounded hover:bg-slate-100"
                                                    >
                                                        <MoreVertical className="w-5 h-5" />
                                                    </button>
                                                    {openDropdownId === employee.id && (
                                                        <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-[9999]">
                                                            <button
                                                                onClick={(e) => {
                                                                    console.log('View Details clicked for:', employee.name);
                                                                    e.stopPropagation();
                                                                    setOpenDropdownId(null);
                                                                    navigate(`/admin/employees/${employee.id}`);
                                                                }}
                                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                                <span>View Details</span>
                                                            </button>
                                                            {employee.status === 'active' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdownId(null);
                                                                        setAssignCurriculumEmployee(employee);
                                                                        setAssignCurriculumId('');
                                                                        setShowAssignCurriculumModal(true);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                                                                >
                                                                    <BookOpen className="w-4 h-4" />
                                                                    <span>Assign Curriculum</span>
                                                                </button>
                                                            )}
                                                            {employee.status === 'active' ? (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdownId(null);
                                                                        setSelectedEmployee(employee);
                                                                        setActionType('deactivate');
                                                                        setShowDeactivateModal(true);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                                                >
                                                                    <UserX className="w-4 h-4" />
                                                                    <span>Deactivate</span>
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={(e) => {
                                                                        console.log('Reactivate clicked for:', employee.name);
                                                                        e.stopPropagation();
                                                                        setOpenDropdownId(null);
                                                                        setSelectedEmployee(employee);
                                                                        setActionType('reactivate');
                                                                        setShowDeactivateModal(true);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center space-x-2"
                                                                >
                                                                    <CheckCircle className="w-4 h-4" />
                                                                    <span>Reactivate</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            <InviteModal 
                show={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                onSubmit={handleInviteSubmit}
            />

            <BulkReminderModal
                show={showBulkReminderModal}
                onClose={() => setShowBulkReminderModal(false)}
                employees={filteredEmployees}
                onSubmit={handleBulkReminderSubmit}
            />

            {/* Deactivate/Reactivate Modal */}
            {showDeactivateModal && selectedEmployee && (
                <div className="fixed inset-0 bg-slate-200 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-semibold text-slate-500">
                                {actionType === 'deactivate' ? 'Deactivate Employee' : 'Reactivate Employee'}
                            </h3>
                            <button onClick={() => setShowDeactivateModal(false)} className="text-slate-500 hover:text-slate-500">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-slate-500 mb-6">
                            {actionType === 'deactivate' 
                                ? `Are you sure you want to deactivate ${selectedEmployee.name}? They will lose access to the system.`
                                : `Are you sure you want to reactivate ${selectedEmployee.name}? They will regain access to the system.`
                            }
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeactivateModal(false)}
                                className="px-4 py-2 text-slate-500 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                            {actionType === 'deactivate' ? (
                                <button
                                    onClick={() => {
                                        handleDeactivate(selectedEmployee);
                                        setShowDeactivateModal(false);
                                    }}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                                >
                                    <UserX className="w-4 h-4 mr-2" />
                                    Deactivate
                                </button>
                            ) : (
                                <button
                                    onClick={() => {
                                        handleReactivate(selectedEmployee);
                                        setShowDeactivateModal(false);
                                    }}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                                >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Reactivate
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Curriculum Modal */}
            {showAssignCurriculumModal && assignCurriculumEmployee && (
                <div className="fixed inset-0 bg-slate-200 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-semibold text-slate-500">
                                Assign Curriculum
                            </h3>
                            <button
                                onClick={() => {
                                    setShowAssignCurriculumModal(false);
                                    setAssignCurriculumEmployee(null);
                                }}
                                className="text-slate-500 hover:text-slate-500"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-slate-500 mb-4">
                            Assign an additional curriculum to{' '}
                            <span className="font-medium">{assignCurriculumEmployee.name}</span>{' '}
                            ({assignCurriculumEmployee.email}).
                        </p>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-slate-500 mb-1">Curriculum</label>
                            <select
                                value={assignCurriculumId}
                                onChange={(e) => setAssignCurriculumId(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="">Select a curriculum</option>
                                <option value="ee20f273-17ba-4635-aaf6-d359e38a4aa2">60 Day Orientation</option>
                                <option value="822991ba-d074-4d68-b32d-ea004ddd9bc8">Annual Training</option>
                            </select>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowAssignCurriculumModal(false);
                                    setAssignCurriculumEmployee(null);
                                }}
                                className="px-4 py-2 text-slate-500 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAssignCurriculum}
                                disabled={!assignCurriculumId || isAssigningCurriculum}
                                className={`px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center ${
                                    !assignCurriculumId || isAssigningCurriculum ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                <BookOpen className="w-4 h-4 mr-2" />
                                {isAssigningCurriculum ? 'Assigning...' : 'Assign'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed bottom-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{successMessage}</span>
                </div>
            )}

            {error && (
                <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}
        </div>
    );
} 